import { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import ActivityIcon from './ActivityIcon';

const PLACE_TYPES = ['place', 'city', 'state', 'country'];

// Add / Edit modal for a place⇄activities mapping.
// - add mode: name (required) + optional place_type + activities (required, ≥1)
// - edit mode: name shown read-only + optional place_type + activities (required, ≥1)
// The parent owns the mutation; it keeps `open` true on error (e.g. 409) and
// passes `serverError` back so the form stays filled in.
export default function PlaceActivityForm({
  open,
  mode, // 'add' | 'edit'
  record,
  catalog,
  catalogLoading,
  catalogError,
  pending,
  serverError,
  onClose,
  onSubmit,
}) {
  const isEdit = mode === 'edit';
  const [name, setName] = useState('');
  const [placeType, setPlaceType] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [touched, setTouched] = useState(false);

  // (Re)seed whenever the modal opens for a different target.
  const seedKey = open ? (isEdit ? `edit-${record?.id}` : 'add') : null;
  const [seededFor, setSeededFor] = useState(null);
  useEffect(() => {
    if (!open) { setSeededFor(null); return; }
    if (seededFor === seedKey) return;
    setName(isEdit ? record?.place_name || '' : '');
    setPlaceType(record?.place_type || '');
    setSelected(new Set((record?.activities || []).map((a) => a.id)));
    setTouched(false);
    setSeededFor(seedKey);
  }, [open, seedKey, seededFor, isEdit, record]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const nameMissing = !isEdit && !name.trim();
  const activitiesMissing = selected.size === 0;
  const invalid = nameMissing || activitiesMissing;

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (invalid) return;
    const activities = Array.from(selected);
    if (isEdit) {
      onSubmit({ activities, ...(placeType && { place_type: placeType }) });
    } else {
      onSubmit({ name: name.trim(), activities, ...(placeType && { place_type: placeType }) });
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? `Edit “${record?.place_name || ''}”` : 'Add mapping'}
      onClose={onClose}
      size="wide"
    >
      <form className="form" onSubmit={submit}>
        {isEdit ? (
          <div className="field">
            <span>Place name</span>
            <p className="muted" style={{ margin: '2px 0 0' }}>{record?.place_name}</p>
          </div>
        ) : (
          <label className="field">
            <span>Place name *</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Goa"
            />
            {touched && nameMissing && <em className="field__error">Place name is required</em>}
          </label>
        )}

        <label className="field">
          <span>Place type</span>
          <select value={placeType} onChange={(e) => setPlaceType(e.target.value)}>
            <option value="">— none —</option>
            {PLACE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>

        <div className="field">
          <span>Activities * <span className="muted">({selected.size} selected)</span></span>
          {catalogLoading ? (
            <div className="state state--loading">Loading activities…</div>
          ) : catalogError ? (
            <div className="state state--error" role="alert">
              {catalogError.message || 'Failed to load activities.'}
            </div>
          ) : (catalog || []).length === 0 ? (
            <div className="state state--empty">No activities in the catalog.</div>
          ) : (
            <div className="act-picker">
              {catalog.map((a) => {
                const on = selected.has(a.id);
                return (
                  <button
                    type="button"
                    key={a.id}
                    className={`act-option${on ? ' act-option--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => toggle(a.id)}
                  >
                    <ActivityIcon activity={a} />
                    <span className="act-option__name">{a.name}</span>
                  </button>
                );
              })}
            </div>
          )}
          {touched && activitiesMissing && (
            <em className="field__error">Select at least one activity</em>
          )}
        </div>

        {serverError && <em className="field__error">{serverError}</em>}

        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={pending || invalid}>
            {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
